import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

export default function ContratarPage() {
  const formContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const whatsappNumber = '56942470111';
  const whatsappMessage = encodeURIComponent('Hola, me interesa conocer más sobre sus servicios de hosting.');

  useEffect(() => {
    const script = document.createElement('script');
    script.charset = 'utf-8';
    script.type = 'text/javascript';
    script.src = '//js.hsforms.net/forms/embed/v2.js';
    script.async = true;

    script.onload = () => {
      if ((window as any).hbspt && formContainerRef.current) {
        (window as any).hbspt.forms.create({
          portalId: '1485795',
          formId: 'aef6dd22-29ea-4229-99bf-3b65dfc57d6d',
          region: 'na1',
          target: '#hs-form-container',
          onFormSubmitted: () => {
            navigate('/contratar/gracias');
          }
        });
      }
    };

    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, [navigate]);

  return (
    <>
      <Helmet>
        <title>Contratar Hosting y Servidores en Chile | Cotización Inmediata Online</title>
        <meta name="description" content="Solicita tu cotización para hosting, VPS o servidores dedicados en Chile. Planes personalizados para empresas en Santiago y regiones. Respuesta inmediata y migración gratuita incluida." />
        <meta name="keywords" content="contratar hosting chile, cotización hosting, comprar servidor chile, planes hosting santiago, contratar vps chile" />
        <link rel="canonical" href="https://www.ehostingchile.com/contratar" />
        <meta property="og:title" content="Contratar Hosting y Servidores en Chile | Cotización Inmediata Online" />
        <meta property="og:description" content="Solicita tu cotización para hosting, VPS o servidores dedicados en Chile. Planes personalizados para empresas." />
        <meta property="og:url" content="https://www.ehostingchile.com/contratar" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-red-50">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-600/5 via-red-500/5 to-pink-500/5"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 text-white mb-6 shadow-lg shadow-orange-500/30">
                <i className="ri-mail-send-line text-4xl"></i>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Contratar Servicios
              </h1>
              <p className="text-xl text-gray-600 mb-4">
                Completa el formulario y nos pondremos en contacto contigo en menos de 24 horas
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {/* HubSpot Form */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-3xl p-10 shadow-2xl border border-gray-100">
                  <div className="mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">
                      Solicitud de Cotización
                    </h2>
                    <p className="text-gray-600">
                      Completa el formulario y te contactaremos a la brevedad
                    </p>
                  </div>
                  <div id="hs-form-container" ref={formContainerRef} className="hs-form-wrapper"></div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    Información de Contacto
                  </h3>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-orange-50 text-orange-600 flex-shrink-0">
                        <i className="ri-phone-line text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Teléfono</h4>
                        <p className="text-gray-600">+56 2 2581 9100</p>
                        <p className="text-sm text-gray-500">Lun - Vie: 9:00 - 18:00</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-teal-50 text-teal-600 flex-shrink-0">
                        <i className="ri-mail-line text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                        <p className="text-gray-600">ventas@ehostingchile.com</p>
                        <p className="text-sm text-gray-500">Respuesta en 24 hrs</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex-shrink-0">
                        <i className="ri-map-pin-line text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Dirección</h4>
                        <p className="text-gray-600">Av. Apoquindo 6410, of 202, Las Condes</p>
                        <p className="text-sm text-gray-500">Santiago, Chile</p>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-gray-200">
                      <a
                        href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
                        target="_blank"
                        rel="nofollow noreferrer"
                        className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold transition-all whitespace-nowrap"
                      >
                        <i className="ri-whatsapp-line text-xl"></i>
                        Escribir por WhatsApp
                      </a>
                    </div>
                  </div>
                </div>

                {/* Map */}
                <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    Nuestra Ubicación
                  </h3>
                  <div className="aspect-video rounded-xl overflow-hidden">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3330.3456789!2d-70.5789!3d-33.4167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x9662cf7f8c8c8c8d%3A0x1234567890abcdef!2sAv.%20Apoquindo%206410%2C%20Las%20Condes%2C%20Regi%C3%B3n%20Metropolitana%2C%20Chile!5e0!3m2!1ses!2scl!4v1234567890"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      title="Ubicación EhostingChile"
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto bg-gradient-to-br from-orange-600 to-red-600 rounded-3xl p-12 text-center text-white shadow-2xl shadow-orange-500/30">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm mb-6">
                <i className="ri-customer-service-2-line text-4xl"></i>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                ¿Tienes Dudas?
              </h2>
              <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
                Nuestro equipo de ventas está disponible para asesorarte y ayudarte a elegir el mejor plan
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <a
                  href="tel:+56225819100"
                  className="px-8 py-4 bg-white text-orange-600 rounded-xl font-bold hover:bg-gray-50 transition-all shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap"
                >
                  Llamar Ahora
                </a>
                <a
                  href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="px-8 py-4 bg-white/20 backdrop-blur-sm text-white rounded-xl font-bold hover:bg-white/30 transition-all border-2 border-white/30 whitespace-nowrap"
                >
                  Iniciar Chat
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
